/**
 * Running fix builder. A guided three-step flow: capture the first line of
 * position, enter the run between the two times, capture the second line of
 * position. The app advances the first LOP by the run and intersects it with
 * the second to give the running fix.
 *
 * The advance method is chosen and shown, because transferring by water track
 * (course steered and speed through the water) and by ground track (course and
 * speed made good over the ground, which already allows for the tide) give
 * different answers, and the student should see which was used.
 *
 * It reuses the shell's mark-pick mechanism, asking it to reopen this sheet
 * (not the fix builder) once a mark is tapped.
 */
import { useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore } from '@/store';
import { useObjectsStore, nextObjectId } from '@/store/objects';
import { buildRunningFix } from '@/chartwork';
import { Sheet } from './Sheet';

type Mark = { position: [number, number]; label: string };

export function RunningFixSheet() {
  const pendingMark = useShellStore((s) => s.pendingMark);
  const armFixPick = useShellStore((s) => s.armFixPick);
  const setPendingMark = useShellStore((s) => s.setPendingMark);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const ownship = useChartPlotterStore((s) => s.ownship);

  const [stage, setStage] = useState<'first' | 'run' | 'second'>('first');
  const [firstMark, setFirstMark] = useState<Mark | null>(null);
  const [firstBrg, setFirstBrg] = useState('');
  const [cog, setCog] = useState(() => String(Math.round(ownship.heading || 0)));
  const [sog, setSog] = useState(() => String(ownship.sog || ownship.stw || 6));
  const [hours, setHours] = useState('1');
  const [method, setMethod] = useState<'water_track' | 'ground_track'>('water_track');
  const [secondBrg, setSecondBrg] = useState('');

  function reset() {
    setStage('first');
    setFirstMark(null);
    setFirstBrg('');
    setSecondBrg('');
    setMethod('water_track');
    setPendingMark(null);
  }

  function setFirst() {
    const b = Number.parseFloat(firstBrg);
    if (!pendingMark || Number.isNaN(b)) return;
    setFirstMark(pendingMark);
    setPendingMark(null);
    setStage('run');
  }

  function compute() {
    const b2 = Number.parseFloat(secondBrg);
    const b1 = Number.parseFloat(firstBrg);
    const c = Number.parseFloat(cog);
    const s = Number.parseFloat(sog);
    const h = Number.parseFloat(hours);
    if (!firstMark || !pendingMark || [b1, b2, c, s, h].some(Number.isNaN)) {
      showToast('Fill in both bearings and the run');
      return;
    }
    const now = Date.now();
    const built = buildRunningFix({
      earlier: {
        markPosition: firstMark.position,
        markLabel: firstMark.label,
        bearing: b1,
        time: { iso: new Date(now).toISOString() },
      },
      run: { cogDeg: c, sogKnots: s, hours: h },
      advanceMethod: method,
      later: {
        markPosition: pendingMark.position,
        markLabel: pendingMark.label,
        bearing: b2,
        time: { iso: new Date(now + h * 3600_000).toISOString() },
      },
      newId: nextObjectId,
    });
    if (!built) {
      showToast('Those lines do not cross, check the bearings');
      return;
    }
    useObjectsStore
      .getState()
      .putObjects([
        built.earlierObs,
        built.earlierLOP,
        built.advancedLOP,
        built.laterObs,
        built.laterLOP,
        built.runningFix,
      ]);
    reset();
    closeSheet();
    showToast('Running fix plotted');
  }

  return (
    <Sheet
      name="runningfix"
      title="Running fix"
      sub="Take a bearing, run on, take another. We carry the first bearing forward by the run and cross it with the second. Best with a single object you pass, like a lighthouse."
    >
      {stage === 'first' && (
        <div>
          <p className="sub" style={{ marginBottom: 8 }}>
            Step 1 of 3. The first line of position, at time one.
          </p>
          {pendingMark ? (
            <>
              <p className="sub" style={{ marginBottom: 8 }}>
                First mark: <b>{pendingMark.label}</b>. Bearing taken on it, degrees true?
              </p>
              <input
                className="acp-note-input"
                style={{ marginTop: 0 }}
                inputMode="decimal"
                placeholder="Bearing at time one"
                value={firstBrg}
                onChange={(e) => setFirstBrg(e.target.value)}
              />
              <div className="acp-btn-row">
                <button type="button" className="acp-btn primary" onClick={setFirst}>
                  Set first bearing
                </button>
              </div>
            </>
          ) : (
            <div className="acp-btn-row">
              <button
                type="button"
                className="acp-btn primary"
                onClick={() => {
                  armFixPick('runningfix');
                  showToast('Tap the first mark');
                }}
              >
                Pick first mark
              </button>
            </div>
          )}
        </div>
      )}

      {stage === 'run' && (
        <div>
          <p className="sub" style={{ marginBottom: 8 }}>
            Step 2 of 3. How do we carry the first bearing forward? By the water track (course
            steered and speed through the water), or by the ground track (course and speed made good
            over the ground, which already allows for the tide). First LOP on{' '}
            <b>{firstMark?.label}</b> at {firstBrg}°.
          </p>
          <div className="acp-seg">
            <button
              type="button"
              className={method === 'water_track' ? 'on' : ''}
              onClick={() => setMethod('water_track')}
            >
              Water track
            </button>
            <button
              type="button"
              className={method === 'ground_track' ? 'on' : ''}
              onClick={() => setMethod('ground_track')}
            >
              Ground track
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Field
              label={method === 'water_track' ? 'Course steered' : 'CMG'}
              value={cog}
              onChange={setCog}
            />
            <Field label={method === 'water_track' ? 'Speed STW' : 'SMG'} value={sog} onChange={setSog} />
            <Field label="Hours" value={hours} onChange={setHours} />
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={reset}>
              Start over
            </button>
            <button type="button" className="acp-btn primary" onClick={() => setStage('second')}>
              Next, second bearing
            </button>
          </div>
        </div>
      )}

      {stage === 'second' && (
        <div>
          <p className="sub" style={{ marginBottom: 8 }}>
            Step 3 of 3. The second line of position, at time two.
          </p>
          {pendingMark ? (
            <>
              <p className="sub" style={{ marginBottom: 8 }}>
                Second mark: <b>{pendingMark.label}</b>. Bearing taken on it, degrees true?
              </p>
              <input
                className="acp-note-input"
                style={{ marginTop: 0 }}
                inputMode="decimal"
                placeholder="Bearing at time two"
                value={secondBrg}
                onChange={(e) => setSecondBrg(e.target.value)}
              />
              <div className="acp-btn-row">
                <button type="button" className="acp-btn" onClick={reset}>
                  Start over
                </button>
                <button type="button" className="acp-btn primary" onClick={compute}>
                  Compute running fix
                </button>
              </div>
            </>
          ) : (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn" onClick={() => setStage('run')}>
                Back
              </button>
              <button
                type="button"
                className="acp-btn primary"
                onClick={() => {
                  armFixPick('runningfix');
                  showToast('Tap the second mark');
                }}
              >
                Pick second mark
              </button>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ flex: 1, fontSize: 11, color: 'var(--ink-soft)' }}>
      {label}
      <input
        className="acp-note-input"
        style={{ marginTop: 2 }}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
